import {
  Email as EmailIcon,
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  Phone as PhoneIcon,
  Twitter as TwitterIcon,
  YouTube as YouTubeIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  ArrowUpward as ArrowUpIcon,
  Send as SendIcon,
  Rocket as RocketIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Support as SupportIcon,
  Language as LanguageIcon,
  GitHub as GitHubIcon,
  Telegram as TelegramIcon,
  WhatsApp as WhatsAppIcon,
  Star as StarIcon,
  TrendingUp as TrendingIcon,
  Analytics as AnalyticsIcon,
  CloudDone as CloudIcon,
  Psychology as BrainIcon,
  Extension as ExtensionIcon,
  DiscountRounded
} from '@mui/icons-material';
import {
  Box,
  Container,
  Divider,
  Grid,
  IconButton,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Paper,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Avatar,
  Card,
  CardContent,
  Link
} from '@mui/material';
import { useState, useEffect } from 'react';

const FooterPremium = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Enhanced scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Mouse tracking for interactive effects
  const handleMouseMove = (e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    console.log('Newsletter subscription:', email);
    setEmail('');
  };

  // Enhanced menu structure
  const footerSections = [
    {
      title: 'Platform',
      icon: <RocketIcon />,
      links: [
        { name: 'AI Content Management', href: '/platform/ai-cms', new: true },
        { name: 'Visual Page Builder', href: '/platform/builder', popular: true },
        { name: 'Mobile Applications', href: '/platform/mobile' },
        { name: 'Analytics Dashboard', href: '/platform/analytics' },
        { name: 'API Documentation', href: '/docs/api' }
      ]
    },
    {
      title: 'Solutions',
      icon: <ExtensionIcon />,
      links: [
        { name: 'News & Media', href: '/solutions/news', popular: true },
        { name: 'E-commerce', href: '/solutions/ecommerce' },
        { name: 'Corporate Websites', href: '/solutions/corporate' },
        { name: 'Education Portals', href: '/solutions/education' },
        { name: 'Healthcare Systems', href: '/solutions/healthcare' }
      ]
    },
    {
      title: 'Resources',
      icon: <CodeIcon />,
      links: [
        { name: 'Documentation', href: '/docs' },
        { name: 'Tutorials', href: '/tutorials' },
        { name: 'Case Studies', href: '/case-studies' },
        { name: 'Webinars', href: '/webinars', new: true },
        { name: 'Community Forum', href: '/community' }
      ]
    },
    {
      title: 'Company',
      icon: <StarIcon />,
      links: [
        { name: 'About Us', href: '/about' },
        { name: 'Careers', href: '/careers' },
        { name: 'Press Kit', href: '/press' },
        { name: 'Contact', href: '/contact' },
        { name: 'Partners', href: '/partners' }
      ]
    }
  ];

  const legalLinks = [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Cookie Policy', href: '/cookies' },
    { name: 'GDPR Compliance', href: '/gdpr' },
    { name: 'Security', href: '/security' }
  ];

  // Enhanced services with gradients
  const services = [
    { 
      icon: <BrainIcon />, 
      title: 'AI-Powered', 
      desc: 'Next-gen intelligence',
      color: '#4f46e5',
      gradient: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
    },
    { 
      icon: <SecurityIcon />, 
      title: 'Enterprise Security', 
      desc: 'Bank-grade protection',
      color: '#059669',
      gradient: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)'
    },
    { 
      icon: <SpeedIcon />, 
      title: 'Ultra Performance', 
      desc: '<150ms global speed',
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    },
    { 
      icon: <SupportIcon />, 
      title: '24/7 Support', 
      desc: 'Always here to help',
      color: '#7c3aed',
      gradient: 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)'
    }
  ];

  // Enhanced social links
  const socialLinks = [
    { icon: <FacebookIcon />, color: '#1877f2', label: 'Facebook', users: '50K+' },
    { icon: <TwitterIcon />, color: '#1da1f2', label: 'Twitter', users: '25K+' },
    { icon: <LinkedInIcon />, color: '#0077b5', label: 'LinkedIn', users: '15K+' },
    { icon: <InstagramIcon />, color: '#e4405f', label: 'Instagram', users: '30K+' },
    { icon: <YouTubeIcon />, color: '#ff0000', label: 'YouTube', users: '100K+' },
    { icon: <GitHubIcon />, color: '#333', label: 'GitHub', users: '5K+' },
    { icon: <DiscountRounded />, color: '#5865f2', label: 'Discord', users: '8K+' },
    { icon: <TelegramIcon />, color: '#0088cc', label: 'Telegram', users: '12K+' }
  ];

  // Stats data
  const stats = [
    { icon: <TrendingIcon />, value: '99.99%', label: 'Uptime', color: '#10b981' },
    { icon: <AnalyticsIcon />, value: '50K+', label: 'Projects', color: '#6366f1' },
    { icon: <CloudIcon />, value: '<150ms', label: 'Load Time', color: '#f59e0b' },
    { icon: <StarIcon />, value: '4.9/5', label: 'Rating', color: '#fbbf24' }
  ];

  return (
    <Box 
      sx={{ 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          width: 500,
          height: 500,
          background: `radial-gradient(circle, rgba(79, 70, 229, 0.03) 0%, transparent 70%)`,
          borderRadius: '50%',
          left: mousePosition.x - 250,
          top: mousePosition.y - 250,
          pointerEvents: 'none',
          transition: 'all 0.3s ease',
          opacity: 0.6,
          zIndex: 1
        }
      }}
    >
      {/* Enhanced Newsletter Section */}
      <Box 
        sx={{ 
          background: `
            linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%),
            radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 75% 75%, rgba(5, 150, 105, 0.1) 0%, transparent 50%)
          `,
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              conic-gradient(from 0deg at 20% 20%, rgba(79, 70, 229, 0.05), transparent, rgba(79, 70, 229, 0.05)),
              conic-gradient(from 90deg at 80% 80%, rgba(5, 150, 105, 0.05), transparent, rgba(5, 150, 105, 0.05))
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} lg={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                    <Chip
                      icon={<RocketIcon />}
                      label="Newsletter"
                      sx={{
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                    <Chip
                      icon={<StarIcon />}
                      label="50K+ Subscribers"
                      sx={{
                        background: 'linear-gradient(135deg, #059669 0%, #0891b2 100%)',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        '& .MuiChip-icon': { color: 'white' }
                      }}
                    />
                  </Stack>

                  <Typography 
                    variant="h2" 
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      fontWeight: 900,
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 4,
                      lineHeight: 1.1
                    }}
                  >
                    Stay Ahead of Innovation
                  </Typography>

                  <Typography 
                    variant="h5" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.8)', 
                      mb: 6, 
                      lineHeight: 1.6,
                      fontSize: { xs: '1.2rem', md: '1.4rem' }
                    }}
                  >
                    Get exclusive insights, feature updates, and industry trends delivered to your inbox. 
                    Join 50,000+ professionals who trust our newsletter.
                  </Typography>
                  
                  <Stack 
                    direction={{ xs: 'column', sm: 'row' }} 
                    spacing={2} 
                    flexWrap="wrap" 
                    gap={2}
                    sx={{ mb: 6 }}
                  >
                    {[
                      { icon: <BrainIcon />, text: 'AI Insights' },
                      { icon: <TrendingIcon />, text: 'Industry Trends' },
                      { icon: <CodeIcon />, text: 'Technical Updates' },
                      { icon: <StarIcon />, text: 'Exclusive Offers' }
                    ].map((item, index) => (
                      <Chip 
                        key={index}
                        icon={item.icon}
                        label={item.text}
                        sx={{ 
                          bgcolor: 'rgba(255,255,255,0.1)', 
                          color: 'white',
                          fontWeight: 600,
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255,255,255,0.2)',
                          fontSize: '0.9rem',
                          '& .MuiChip-icon': { color: 'rgba(255,255,255,0.8)' },
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,0.15)',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.3s ease'
                        }} 
                      />
                    ))}
                  </Stack>

                  {/* Stats */}
                  <Grid container spacing={4}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} md={3} key={index}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              p: 2,
                              borderRadius: 3,
                              bgcolor: `rgba(255,255,255,0.1)`,
                              color: stat.color,
                              mb: 2,
                              border: '1px solid rgba(255,255,255,0.1)',
                              backdropFilter: 'blur(10px)'
                            }}
                          >
                            {stat.icon}
                          </Box>
                          <Typography 
                            variant="h4" 
                            sx={{ 
                              fontWeight: 900, 
                              color: 'white',
                              mb: 0.5
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: 'rgba(255,255,255,0.7)',
                              fontWeight: 600
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} lg={6}>
              <Slide direction="left" in timeout={1200}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, md: 6 },
                    background: 'rgba(255,255,255,0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: 6,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: `
                        radial-gradient(circle at 30% 30%, rgba(79, 70, 229, 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 70% 70%, rgba(5, 150, 105, 0.1) 0%, transparent 50%)
                      `,
                      pointerEvents: 'none'
                    }
                  }}
                >
                  <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Typography 
                      variant="h4" 
                      sx={{
                        fontWeight: 800,
                        color: 'white', 
                        mb: 4,
                        textAlign: 'center',
                        fontSize: { xs: '1.8rem', md: '2.2rem' }
                      }}
                    >
                      🚀 Join the Innovation
                    </Typography>
                    
                    <Box component="form" onSubmit={handleNewsletterSubmit}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Enter your professional email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        sx={{
                          mb: 4,
                          '& .MuiOutlinedInput-root': {
                            bgcolor: 'rgba(255,255,255,0.9)',
                            borderRadius: 4,
                            fontSize: '1.1rem',
                            '& fieldset': {
                              borderColor: 'transparent'
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(79, 70, 229, 0.5)'
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#4f46e5',
                              borderWidth: 2
                            }
                          },
                          '& .MuiInputBase-input': {
                            py: 3
                          }
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton 
                                type="submit"
                                sx={{ 
                                  color: '#4f46e5',
                                  bgcolor: 'rgba(79, 70, 229, 0.1)',
                                  '&:hover': { 
                                    bgcolor: 'rgba(79, 70, 229, 0.2)',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.3s ease'
                                }}
                              >
                                <SendIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                      
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        startIcon={<SendIcon />}
                        sx={{
                          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                          color: 'white',
                          fontWeight: 800,
                          py: 3,
                          borderRadius: 4,
                          textTransform: 'none',
                          fontSize: '1.2rem',
                          boxShadow: '0 8px 25px rgba(79, 70, 229, 0.3)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #3730a3 0%, #6b21a8 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 12px 35px rgba(79, 70, 229, 0.4)'
                          },
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Subscribe Now - It's Free
                      </Button>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      sx={{
                        mt: 4,
                        color: 'rgba(255,255,255,0.6)',
                        textAlign: 'center',
                        lineHeight: 1.6
                      }}
                    >
                      ✅ No spam, ever • ✅ Unsubscribe anytime • ✅ 50K+ happy subscribers
                    </Typography>
                  </Box>
                </Paper>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Enhanced Main Footer */}
      <Box 
        sx={{ 
          background: `
            linear-gradient(180deg, #0a0a0f 0%, #1a1a2e 50%, #16213e 100%)
          `,
          color: 'white', 
          py: { xs: 10, md: 16 },
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 25% 25%, rgba(79, 70, 229, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(5, 150, 105, 0.05) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8}>
            {/* Enhanced Company Info */}
            <Grid item xs={12} lg={4}>
              <Box sx={{ mb: 6 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: 4,
                      background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 25px rgba(79, 70, 229, 0.3)'
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 900,
                        color: 'white',
                        letterSpacing: '-1px'
                      }}
                    >
                      R
                    </Typography>
                  </Box>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{
                        fontWeight: 900,
                        background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: '2.5rem',
                        lineHeight: 1
                      }}
                    >
                      RONESIS
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.6)',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        mt: 0.5
                      }}
                    >
                      Enterprise Platform
                    </Typography>
                  </Box>
                </Box>

                <Typography 
                  variant="body1" 
                  sx={{ 
                    mb: 6, 
                    color: 'rgba(255,255,255,0.8)', 
                    lineHeight: 1.8,
                    fontSize: '1.1rem'
                  }}
                >
                  Leading the future of digital publishing with AI-powered solutions. 
                  Trusted by 50,000+ enterprises worldwide since 2010. 
                  Building tomorrow's media experiences today.
                </Typography>
                
                {/* Enhanced Services Grid */}
                <Grid container spacing={3}>
                  {services.map((service, index) => (
                    <Grid item xs={6} key={index}>
                      <Card
                        elevation={0}
                        sx={{
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 4,
                          textAlign: 'center',
                          transition: 'all 0.4s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            background: `${service.color}10`,
                            borderColor: `${service.color}30`,
                            transform: 'translateY(-8px)',
                            boxShadow: `0 12px 30px ${service.color}20`
                          }
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box 
                            sx={{ 
                              color: service.color, 
                              mb: 2,
                              display: 'inline-flex',
                              p: 2,
                              borderRadius: 3,
                              background: `${service.color}15`
                            }}
                          >
                            {service.icon}
                          </Box>
                          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            {service.title}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: 'rgba(255,255,255,0.6)',
                              fontSize: '0.8rem'
                            }}
                          >
                            {service.desc}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>

            {/* Enhanced Footer Sections */}
            {footerSections.map((section, sectionIndex) => (
              <Grid item xs={12} sm={6} lg={2} key={sectionIndex}>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        p: 1.5,
                        borderRadius: 2,
                        background: 'rgba(79, 70, 229, 0.1)',
                        color: '#4f46e5'
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{
                        fontWeight: 800,
                        color: '#4f46e5'
                      }}
                    >
                      {section.title}
                    </Typography>
                  </Box>
                  
                  <Stack spacing={2}>
                    {section.links.map((link, linkIndex) => (
                      <Box key={linkIndex}>
                        <Link
                          href={link.href}
                          sx={{
                            color: 'rgba(255,255,255,0.7)',
                            textDecoration: 'none',
                            fontSize: '1rem',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            position: 'relative',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              color: '#42a5f5',
                              transform: 'translateX(8px)'
                            },
                            '&::before': {
                              content: '"→"',
                              position: 'absolute',
                              left: -20,
                              opacity: 0,
                              transition: 'opacity 0.3s ease'
                            },
                            '&:hover::before': {
                              opacity: 1
                            }
                          }}
                        >
                          {link.name}
                          {link.new && (
                            <Chip 
                              label="New" 
                              size="small" 
                              sx={{ 
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: '#10b981',
                                color: 'white',
                                fontWeight: 700
                              }} 
                            />
                          )}
                          {link.popular && (
                            <Chip 
                              label="Popular" 
                              size="small" 
                              sx={{ 
                                height: 18,
                                fontSize: '0.65rem',
                                bgcolor: '#f59e0b',
                                color: 'white',
                                fontWeight: 700
                              }} 
                            />
                          )}
                        </Link>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              </Grid>
            ))}

            {/* Enhanced Contact Info */}
            <Grid item xs={12} lg={4}>
              <Typography 
                variant="h5" 
                sx={{
                  fontWeight: 800,
                  color: '#4f46e5', 
                  mb: 4,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <LocationIcon />
                Get In Touch
              </Typography>
              
              <Stack spacing={4} sx={{ mb: 6 }}>
                {[
                  {
                    icon: <PhoneIcon />,
                    title: '+90 (212) 322 74 90',
                    subtitle: 'Monday - Friday: 09:00 - 18:00',
                    color: '#4f46e5'
                  },
                  {
                    icon: <EmailIcon />,
                    title: 'enterprise@ronesis.com',
                    subtitle: '24/7 enterprise support',
                    color: '#059669'
                  },
                  {
                    icon: <LocationIcon />,
                    title: 'Istanbul, Turkey',
                    subtitle: 'Global headquarters',
                    color: '#f59e0b'
                  }
                ].map((contact, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 3,
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: 4,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        background: `${contact.color}10`,
                        borderColor: `${contact.color}30`,
                        transform: 'translateY(-4px)'
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box
                        sx={{
                          background: `${contact.color}20`,
                          borderRadius: 3,
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: contact.color
                        }}
                      >
                        {contact.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" fontWeight={700} color="white">
                          {contact.title}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ color: 'rgba(255,255,255,0.6)' }}
                        >
                          {contact.subtitle}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Stack>

              {/* Enhanced Social Media */}
              <Typography 
                variant="h6" 
                sx={{
                  fontWeight: 800,
                  color: '#4f46e5', 
                  mb: 3
                }}
              >
                Connect With Us
              </Typography>
              
              <Grid container spacing={2}>
                {socialLinks.map((social, index) => (
                  <Grid item xs={3} key={index}>
                    <Box
                      sx={{
                        position: 'relative',
                        textAlign: 'center'
                      }}
                    >
                      <IconButton
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          color: 'white',
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            bgcolor: social.color,
                            borderColor: social.color,
                            transform: 'translateY(-8px) scale(1.1)',
                            boxShadow: `0 12px 30px ${social.color}40`
                          }
                        }}
                        aria-label={social.label}
                      >
                        {social.icon}
                      </IconButton>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          mt: 1,
                          display: 'block'
                        }}
                      >
                        {social.users}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Enhanced Bottom Bar */}
      <Box 
        sx={{ 
          bgcolor: '#000', 
          py: 4,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          position: 'relative'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  fontWeight: 500
                }}
              >
                © 2025 RONESIS Enterprise Platform. All rights reserved.
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255,255,255,0.5)',
                  mt: 1
                }}
              >
                Designed & Developed with ❤️ by RONESIS Team
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box 
                sx={{ 
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  flexWrap: 'wrap',
                  gap: 3
                }}
              >
                {legalLinks.map((link, index) => (
                  <Link
                    key={index}
                    href={link.href}
                    sx={{
                      color: 'rgba(255,255,255,0.6)',
                      textDecoration: 'none',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'color 0.3s ease',
                      '&:hover': {
                        color: '#42a5f5'
                      }
                    }}
                  >
                    {link.name}
                  </Link>
                ))}
              </Box>
              
              <Box 
                sx={{ 
                  display: 'flex',
                  justifyContent: { xs: 'flex-start', md: 'flex-end' },
                  gap: 3,
                  mt: 2
                }}
              >
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  v3.0.0 Enterprise
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  Last updated: Dec 2025
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Enhanced Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: 40,
            right: 40,
            zIndex: 1000,
            borderRadius: 4,
            overflow: 'hidden'
          }}
        >
          <IconButton
            onClick={scrollToTop}
            sx={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              color: 'white',
              width: 70,
              height: 70,
              borderRadius: 4,
              boxShadow: '0 8px 25px rgba(79, 70, 229, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #3730a3 0%, #6b21a8 100%)',
                transform: 'translateY(-4px) scale(1.1)',
                boxShadow: '0 12px 35px rgba(79, 70, 229, 0.6)'
              },
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: -100,
                width: 100,
                height: '100%',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                animation: 'shimmer 2s ease-in-out infinite'
              }
            }}
          >
            <ArrowUpIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
        </Paper>
      </Fade>

      {/* CSS Animations */}
      <style jsx global>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%) skewX(-15deg); }
          100% { transform: translateX(200%) skewX(-15deg); }
        }
      `}</style>
    </Box>
  );
};

export default FooterPremium;
